import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';

const LEGAL_NAV = [
    { path: '/privacy', label: 'Privacy Policy' },
    { path: '/ai-policy', label: 'AI Policy' },
    { path: '/data-policy', label: 'Data Policy' },
    { path: '/terms', label: 'Terms & Conditions' },
];

export default function LegalLayout({ title, lastUpdated, children }) {
    const location = useLocation();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Header */}
            <div className="border-b border-stone-200 bg-white">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-stone-900 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Nurture <span className="text-stone-500">CRM</span>
                        </span>
                    </Link>
                    <Link to="/" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900 transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back to Home
                    </Link>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-10 flex gap-10">
                {/* Sidebar Nav */}
                <nav className="hidden md:block w-56 flex-shrink-0">
                    <div className="sticky top-10 space-y-1">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 px-3">Legal</h3>
                        {LEGAL_NAV.map(item => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                    location.pathname === item.path
                                        ? 'bg-stone-900 text-white font-medium'
                                        : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                                }`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </nav>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {/* Mobile Nav */}
                    <div className="md:hidden flex flex-wrap gap-2 mb-6">
                        {LEGAL_NAV.map(item => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                    location.pathname === item.path
                                        ? 'bg-stone-900 text-white'
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    <div className="bg-white rounded-xl border border-stone-200 p-8 sm:p-10">
                        <h1 className="text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            {title}
                        </h1>
                        {lastUpdated && (
                            <p className="text-sm text-stone-400 mb-8">Last updated: {lastUpdated}</p>
                        )}
                        <div className="prose prose-stone prose-sm max-w-none
                            prose-headings:font-semibold prose-headings:text-stone-900
                            prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                            prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
                            prose-p:text-stone-600 prose-p:leading-relaxed
                            prose-li:text-stone-600
                            prose-strong:text-stone-900
                            prose-a:text-stone-900 prose-a:underline
                        ">
                            {children}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center text-xs text-stone-400 space-y-1">
                        <p>NurtureCRM.ai is a product of <a href="https://multiplai.tech" className="underline hover:text-stone-600">Multiplai.tech</a></p>
                        <p>AiCoaches dot com LLC | 111 NE 1ST ST, 8TH FLOOR 89145, Miami, Florida, USA 33132</p>
                        <p>Legal inquiries: <a href="mailto:support@aicoaches.com" className="underline hover:text-stone-600">support@aicoaches.com</a></p>
                    </div>
                </div>
            </div>
        </div>
    );
}
